import {
  LitElement,
  html,
  css,
  unsafeHTML,
  utils,
  paths,
  t,
  broadcastReceiver,
} from '@hh/global.js'

class HHModal extends LitElement {
  static properties = {
    id: {},
    open: {
      reflect: true,
    },
    contentAriaLabel: { type: String },
    defaultCloseBtn: { type: String, attribute: 'default-close-btn' },
    isGwpModal: { type: Boolean, attribute: 'is-gwp-modal' },
    isMultipleGwp: { type: Boolean, attribute: 'multiple-gifts' },
  }

  constructor() {
    super()

    this.contentAriaLabel = this.dataset.ariaLabel || t('products.modal.label')
    this.modalCloseSelector =
      this.dataset.modalCloseSelector || '.js-close-modal'
    this.isGwpModal = false
    this.isMultipleGwp = false

    this.defaultCloseBtn = ''
  }

  requestUpdate(name, oldValue) {
    if (name === 'open' && !!this[name]) {
      utils.pauseAllMedia()
    }

    return super.requestUpdate(name, oldValue)
  }

  connectedCallback() {
    super.connectedCallback()

    this.receiver = broadcastReceiver('modal', (...args) => {
      this.openFromDocument(...args)
    })

    this.querySelectorAll(this.modalCloseSelector).forEach((el) => {
      el.addEventListener('click', (...args) => this.close(...args))
    })

    document.addEventListener('close-modal', () => {
      this.open = false
    })

    if (this.isGwpModal && this.isMultipleGwp) {
      this.updateGwpButton()
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    document.body.classList.remove('overflow-hidden')
    document.removeEventListener('close-modal', () => {
      this.open = false
      this.close()
    })
    this.receiver?.disconnect()
  }

  async updateGwpButton(data) {
    const fromEvent = data !== undefined

    const cart = await utils.getCart()
    const gwpItems = cart.items
      .filter((item) => item.properties?._gwp)
      .map((item) => item.id)

    const offerIds = this.closest('.cart-gwp__offer')
      .dataset.offerProducts.split(',')
      .map((id) => parseInt(id))
    if (offerIds.length < 1) return false

    gwpItems.filter((id) => {
      if (offerIds.includes(id)) {
        this.querySelector(`hh-button[data-product-id="${id}"]`)?.classList.add(
          'selected'
        )

        if (fromEvent) {
          const newItems = [
            ...new Set(
              `${
                sessionStorage.getItem('multipleSelectedGWP')
                  ? `${sessionStorage.getItem('multipleSelectedGWP')},`
                  : ''
              }${id}`.split(',')
            ),
          ]

          sessionStorage.setItem(
            'multipleSelectedGWP',
            `${[...newItems].join(',')}`
          )
        }
      } else {
        this.querySelector(
          `hh-button[data-product-id="${id}"]`
        )?.classList.remove('selected')

        if (fromEvent) {
          const newItems = [
            ...new Set(
              `${
                sessionStorage.getItem('multipleSelectedGWP')
                  ? `${sessionStorage.getItem('multipleSelectedGWP')}`
                  : ''
              }`.split(',')
            ),
          ]

          sessionStorage.setItem(
            'multipleSelectedGWP',
            `${[...newItems]
              .filter((_id) => _id !== id && _id !== null)
              .join(',')}`
          )
        }
      }
    })

    return
  }

  openFromDocument(data) {
    if (this.id === data.id) {
      this.open = true

      if (this.isGwpModal && this.isMultipleGwp) {
        this.updateGwpButton(data)
      }
    } else {
      this.open = false
      return
    }

    if (data.element) {
      setTimeout(() => {
        this.querySelector(`[id="${data.element}"]`)?.scrollIntoView()
      }, 0)
    }
  }

  close(event) {
    document.body.classList.remove('overflow-hidden')
    sessionStorage.removeItem('multipleSelectedGWP')
    if (event.type === 'click') {
      this.open = false
      return
    }
    if (event.key === 'Escape') this.open = false
  }

  handleBodyClick(e) {
    const path = e.composedPath()
    if (
      path.some((node) => {
        return node.matches && node.matches('.overlay')
      })
    ) {
      return true
    }

    if (!e.defaultPrevented && this.open) {
      this.open = false
      return
    }
  }

  render() {
    return html`<div
      @keyup="${this.close}"
      role="dialog"
      aria-modal="true"
      tabindex="-1"
      aria-label="${this.contentAriaLabel}"
    >
      <div class="overlay" @click="${this.close}"></div>
      <div
        class="content"
        role="document"
        tabindex="0"
      >
        <hh-focus-trap active="${this.open}">
          <button
            @click="${this.close}"
            class="button"
            type="button"
            aria-label="${t('accessibility.close')}"
            tabindex="${this.defaultCloseBtn == 'disable' ? '-1' : '0'}"
          >
            ${unsafeHTML(paths.close)}
          </button>
          <slot></slot>
        </hh-focus-trap>
      </div>
    </div>`
  }

  static styles = css`
    :host {
      background-color: var(--hh-background);
      box-sizing: border-box;
      height: 100%;
      left: 0;
      opacity: 0;
      position: fixed;
      top: 0;
      width: 100%;
      visibility: hidden;
      z-index: -1;
    }

    :host([size='flyout']) {
      max-width: var(--hh-max-width, 400px);
      height: auto;
      top: var(--hh-flyout-offset, 30px);
      right: var(--hh-flyout-offset, 30px);
      left: revert;
      border: 0.1rem solid var(--hh-border-color);
    }

    :host([open='true']) {
      opacity: 1;
      visibility: visible;
      z-index: 1000;
    }

    :host(:not([size='flyout'])) .overlay {
      position: fixed;
      width: 100%;
      height: 100%;
      top: 0;
      left: 0;
      background-color: var(--hh-background, var(--hh-color-day));
      z-index: 0;
    }

    .button {
      display: var(--hh-close-btn-display, flex);
      width: 1.2rem;
      position: absolute;
      top: 2.5rem;
      right: 2.5rem;
      background-color: transparent;
      border: 0;
      color: var(--hh-color);
      align-items: center;
      justify-content: center;
      cursor: pointer;
      padding: var(--hh-padding, 1.2rem);
      z-index: 3;
    }

    :host([size='flyout']) .button {
      position: absolute;
      top: var(--hh-close-top, 10px);
      right: var(--hh-close-right, 10px);
    }

    .content {
      position: relative;
      display: flex;
      flex-direction: column;
      height: 100%;
      overflow: auto;
      width: 100%;
      padding: var(--hh-padding);
    }

    :host([size='none']) .content {
      position: unset;
      height: unset;
      width: unset;
    }

    :host([size='flyout']) .content {
      height: auto;
    }

    ::slotted(*) {
      width: var(--hh-slotted-width, auto);
      height: var(--hh-slotted-height, auto);
      margin: var(--hh-slotted-margin);
    }
  `
}

customElements.define('hh-modal', HHModal)
